import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  LinearProgress,
  Collapse,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { ExpandMore, ExpandLess, Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AxiosInstance from "../../services/networkServices";
import useDebounce from "./useDebounce";
import { makeStyles } from "@mui/styles";
import PageLayout from "../../components/PageLayout";
import CustomSwitch from "../../components/Switch";
import { blockUser } from "../../http/auth";
import StatusFilter from "../../components/StatusFilter";
import CommonPagination from "../../components/Table/CommonPagination";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DatePicker, Select } from "antd";

dayjs.extend(utc);
dayjs.extend(timezone);
const { RangePicker } = DatePicker;

const PAGE_SIZE = 50;
const STATS_FETCH_PAGE_SIZE = 500;

const AGE_BRACKETS = [
  { label: "All Ages", min: 0, max: 999 },
  { label: "13-17", min: 13, max: 17 },
  { label: "18-24", min: 18, max: 24 },
  { label: "25-34", min: 25, max: 34 },
  { label: "35-44", min: 35, max: 44 },
  { label: "45+", min: 45, max: 999 },
];

const mapUserData = (user) => ({
  id: user._id,
  firstName: user.firstName,
  lastName: user.lastName,
  username: user.username,
  email: user.email,
  phoneNumber: user.phone,
  age: user.age,
  gender: user.gender,
  isDisabled: user.isAccountBlocked,
  createdAt: user.createdAt,
});

const hasSpecifiedGender = (user) => {
  const gender = user.gender?.toString().trim().toLowerCase();
  if (
    !gender ||
    gender === "n/a" ||
    gender === "na" ||
    gender === "none" ||
    gender === "unknown"
  ) {
    return false;
  }
  return true;
};

const isProfileComplete = (user) => {
  if (!hasSpecifiedGender(user)) return false;
  if (!user.age) return false;
  return true;
};

const applyClientFilters = (
  users,
  { dateRange, genderFilter, ageFilter, profileFilter, timeZone },
) => {
  let result = [...users];

  if (dateRange[0] && dateRange[1]) {
    const startDate = dayjs(dateRange[0]).startOf("day");
    const endDate = dayjs(dateRange[1]).endOf("day");
    result = result.filter((user) => {
      const userDate =
        timeZone === "UTC"
          ? dayjs(user.createdAt).utc()
          : dayjs(user.createdAt).tz("America/New_York");
      return (
        (userDate.isAfter(startDate) || userDate.isSame(startDate, "day")) &&
        (userDate.isBefore(endDate) || userDate.isSame(endDate, "day"))
      );
    });
  }

  if (genderFilter !== "all") {
    result = result.filter((user) => {
      const gender = user.gender?.toString().trim().toLowerCase();
      if (genderFilter === "male") {
        return gender === "male" || gender === "m" || gender === "man";
      }
      if (genderFilter === "female") {
        return gender === "female" || gender === "f" || gender === "woman";
      }
      if (genderFilter === "other") {
        return (
          gender &&
          gender !== "male" &&
          gender !== "m" &&
          gender !== "man" &&
          gender !== "female" &&
          gender !== "f" &&
          gender !== "woman"
        );
      }
      return true;
    });
  }

  if (ageFilter !== 0) {
    const bracket = AGE_BRACKETS[ageFilter];
    result = result.filter((user) => {
      const age = parseInt(user.age) || 0;
      return age >= bracket.min && age <= bracket.max;
    });
  }

  if (profileFilter !== "all") {
    result = result.filter((user) => {
      const complete = isProfileComplete(user);
      return profileFilter === "yes" ? complete : !complete;
    });
  }

  return result;
};

const computeDemographicStats = (users) => {
  const total = users.length;
  const genderCounts = { male: 0, female: 0, other: 0, unknown: 0 };
  users.forEach((user) => {
    const gender = user.gender?.toString().trim().toLowerCase();
    if (!gender) {
      genderCounts.unknown++;
    } else if (gender === "male" || gender === "m" || gender === "man") {
      genderCounts.male++;
    } else if (gender === "female" || gender === "f" || gender === "woman") {
      genderCounts.female++;
    } else {
      genderCounts.other++;
    }
  });

  const ageCounts = {
    "13-17": 0,
    "18-24": 0,
    "25-34": 0,
    "35-44": 0,
    "45+": 0,
    unknown: 0,
  };
  users.forEach((user) => {
    const age = parseInt(user.age) || 0;
    if (age === 0) ageCounts.unknown++;
    else if (age >= 13 && age <= 17) ageCounts["13-17"]++;
    else if (age >= 18 && age <= 24) ageCounts["18-24"]++;
    else if (age >= 25 && age <= 34) ageCounts["25-34"]++;
    else if (age >= 35 && age <= 44) ageCounts["35-44"]++;
    else if (age >= 45) ageCounts["45+"]++;
    else ageCounts.unknown++;
  });

  return { total, genderCounts, ageCounts };
};

const StatsLoadingPlaceholder = () => (
  <Box
    sx={{
      padding: "16px",
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      gap: "24px",
    }}
  >
    {[0, 1].map((column) => (
      <Box key={column} sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="60%" height={28} sx={{ mb: 2 }} />
        {[0, 1, 2, 3].map((row) => (
          <Box key={row} sx={{ mb: "12px" }}>
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton
              variant="rounded"
              height={8}
              sx={{ borderRadius: 4 }}
            />
          </Box>
        ))}
      </Box>
    ))}
  </Box>
);

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    overflowX: "auto",
    maxHeight: "min(55vh, 520px)",
    minHeight: 200,
    boxShadow: "none !important",
    width: "100%",
    maxWidth: "100%",
  },
  table: {
    width: "100%",
    minWidth: 960,
    tableLayout: "fixed",
    border: "1px solid rgba(224, 224, 224, 1)",
  },
  stickyHeader: {
    position: "sticky",
    top: "-1px",
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
  actionsCell: {
    textAlign: "center",
  },
  actionsHeader: {
    textAlign: "center",
    fontWeight: "bold",
  },
  searchField: {
    width: "300px",
    "& .MuiInputLabel-root": {
      color: "#142C5C",
      borderRadius: "15px",
      height: "50px",
    },
    "& .MuiInputBase-formControl": {
      padding: "15px",
      display: "flex",
      alignItems: "center",
      "& input": {
        padding: 0,
      },
      "& fieldset": {
        borderColor: "#142C5C",
      },
      "&:hover fieldset": {
        borderColor: "#142C5C",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#142C5C",
      },
    },
  },
}));

const GENDER_OPTIONS = [
  { label: "All Genders", value: "all" },
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Other", value: "other" },
];

const ManageUsers = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsUsers, setStatsUsers] = useState([]);
  const [filter, setFilter] = useState(0);

  const [dateRange, setDateRange] = useState([null, null]);
  const [genderFilter, setGenderFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState(0);
  const [showStats, setShowStats] = useState(true);
  const [timeZone, setTimeZone] = useState("EST");
  const [profileFilter, setProfileFilter] = useState("all");

  // Reset to first page when server-side filters change (avoids empty later pages).
  useEffect(() => {
    setPage((prev) => (prev === 0 ? prev : 0));
  }, [debouncedSearch, filter]);

  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        let payload = {
          page: page + 1,
          limit: PAGE_SIZE,
          search: debouncedSearch,
        };
        if (filter !== 0) {
          payload = { ...payload, filter };
        }

        const response = await AxiosInstance.post("/admin/list-users", payload);
        if (cancelled) return;

        if (response.data?.data) {
          const responseData = response.data.data;
          const usersData = (responseData.list || []).map(mapUserData);
          setUsers(usersData);
          setTotalUserCount(responseData.resultCount ?? usersData.length);
          setError(null);
        } else {
          setUsers([]);
          setTotalUserCount(0);
          toast.error("No data received from server.");
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        toast.error(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, filter, page]);

  // Background fetch all users matching server filters for demographic stats.
  useEffect(() => {
    let cancelled = false;

    const fetchAllForStats = async () => {
      setStatsLoading(true);
      setStatsUsers([]);

      try {
        let pageNum = 1;
        let allUsers = [];
        let total = 0;

        while (!cancelled) {
          const payload = {
            page: pageNum,
            limit: STATS_FETCH_PAGE_SIZE,
            search: debouncedSearch,
            ...(filter !== 0 ? { filter } : {}),
          };

          const response = await AxiosInstance.post("/admin/list-users", payload);
          if (cancelled) return;

          const responseData = response.data?.data;
          const batch = (responseData?.list || []).map(mapUserData);
          total = responseData?.resultCount ?? batch.length;
          allUsers = allUsers.concat(batch);

          if (
            batch.length < STATS_FETCH_PAGE_SIZE ||
            allUsers.length >= total
          ) {
            break;
          }
          pageNum += 1;
        }

        if (!cancelled) {
          setStatsUsers(allUsers);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.message || "Failed to load demographic stats");
        }
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
        }
      }
    };

    fetchAllForStats();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, filter]);

  const clientFilterParams = useMemo(
    () => ({
      dateRange,
      genderFilter,
      ageFilter,
      profileFilter,
      timeZone,
    }),
    [dateRange, genderFilter, ageFilter, profileFilter, timeZone],
  );

  const demographicStats = useMemo(() => {
    const filtered = applyClientFilters(statsUsers, clientFilterParams);
    return computeDemographicStats(filtered);
  }, [statsUsers, clientFilterParams]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates || [null, null]);
  };

  const handleGenderFilterChange = (value) => {
    setGenderFilter(value);
  };

  const handleAgeFilterChange = (value) => {
    setAgeFilter(value);
  };

  const handleProfileFilterChange = (value) => {
    setProfileFilter(value);
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setGenderFilter("all");
    setAgeFilter(0);
    setProfileFilter("all");
  };

  const handleStatusChange = async (userId) => {
    setLoading(true);
    try {
      await blockUser(userId);
      const response = await AxiosInstance.post("/admin/list-users", {
        page: page + 1,
        limit: PAGE_SIZE,
        search: debouncedSearch,
        ...(filter !== 0 ? { filter } : {}),
      });
      if (response.data?.data) {
        const responseData = response.data.data;
        setUsers((responseData.list || []).map(mapUserData));
        setTotalUserCount(responseData.resultCount ?? 0);
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const headerCellStyle = {
    fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "15px" },
    color: "#000",
    fontWeight: 600,
    padding: { xs: "10px 8px", sm: "12px 10px", md: "14px 12px" },
    backgroundColor: "#e0e0e0",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const bodyCellStyle = {
    fontSize: { xs: "11px", sm: "12px", md: "13px", lg: "14px", xl: "14px" },
    color: "#333",
    padding: { xs: "8px 8px", sm: "10px", md: "12px" },
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const hasClientFilters =
    dateRange[0] ||
    genderFilter !== "all" ||
    ageFilter !== 0 ||
    profileFilter !== "all";

  return (
    <PageLayout title="Users" loading={loading}>
      <ToastContainer />
      {error && <Typography color="error">{error}</Typography>}
      <>
        <Box
          sx={{
            padding: { xs: "12px", sm: "15px" },
            borderRadius: "6px",
            boxShadow:
              "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "1 1 100%", lg: "0 1 auto" },
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "12px", sm: "14px", md: "15px" },
                  paddingRight: "10px",
                  whiteSpace: "nowrap",
                }}
              >
                Status:
              </Typography>
              <StatusFilter isUser={true} setPostFilter={setFilter} />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "1 1 100%", lg: "1 1 200px" },
                minWidth: { xs: "100%", sm: 200 },
              }}
            >
              <input
                placeholder="Search"
                onChange={handleSearchChange}
                value={search}
                type="text"
                className="search-field-ui"
                style={{ width: "100%", maxWidth: "100%", marginRight: 0 }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "1 1 100%", lg: "0 1 auto" },
                flexWrap: "wrap",
                gap: "8px",
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "12px", sm: "14px", md: "15px" },
                  paddingRight: "10px",
                  whiteSpace: "nowrap",
                }}
              >
                Date Range:
              </Typography>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                style={{ minWidth: "200px", maxWidth: "100%", flex: 1 }}
                allowClear
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: "15px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "1 1 100%", sm: "0 1 auto" },
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "12px", sm: "14px", md: "15px" },
                  paddingRight: "10px",
                  whiteSpace: "nowrap",
                }}
              >
                Gender:
              </Typography>
              <Select
                value={genderFilter}
                onChange={handleGenderFilterChange}
                style={{ minWidth: "140px" }}
                options={GENDER_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "1 1 100%", sm: "0 1 auto" },
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "12px", sm: "14px", md: "15px" },
                  paddingRight: "10px",
                  whiteSpace: "nowrap",
                }}
              >
                Age:
              </Typography>
              <Select
                value={ageFilter}
                onChange={handleAgeFilterChange}
                style={{ minWidth: "140px" }}
                options={AGE_BRACKETS.map((bracket, idx) => ({
                  value: idx,
                  label: bracket.label,
                }))}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "1 1 100%", sm: "0 1 auto" },
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "12px", sm: "14px", md: "15px" },
                  paddingRight: "10px",
                  whiteSpace: "nowrap",
                }}
              >
                Profile:
              </Typography>
              <Select
                value={profileFilter}
                onChange={handleProfileFilterChange}
                style={{ minWidth: "160px" }}
                options={[
                  { value: "all", label: "All Profiles" },
                  { value: "yes", label: "Completed" },
                  { value: "no", label: "Incomplete" },
                ]}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "1 1 100%", sm: "0 1 auto" },
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "12px", sm: "14px", md: "15px" },
                  paddingRight: "10px",
                  whiteSpace: "nowrap",
                }}
              >
                Timezone:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  borderRadius: "6px",
                  overflow: "hidden",
                  border: "1px solid #d9d9d9",
                }}
              >
                <Box
                  onClick={() => setTimeZone("EST")}
                  sx={{
                    padding: "4px 14px",
                    fontSize: { xs: "12px", sm: "13px", md: "14px" },
                    fontWeight: 600,
                    cursor: "pointer",
                    backgroundColor: timeZone === "EST" ? "#142C5C" : "#fff",
                    color: timeZone === "EST" ? "#fff" : "#333",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor:
                        timeZone === "EST" ? "#142C5C" : "#f0f0f0",
                    },
                  }}
                >
                  EST
                </Box>
                <Box
                  onClick={() => setTimeZone("UTC")}
                  sx={{
                    padding: "4px 14px",
                    fontSize: { xs: "12px", sm: "13px", md: "14px" },
                    fontWeight: 600,
                    cursor: "pointer",
                    backgroundColor: timeZone === "UTC" ? "#142C5C" : "#fff",
                    color: timeZone === "UTC" ? "#fff" : "#333",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor:
                        timeZone === "UTC" ? "#142C5C" : "#f0f0f0",
                    },
                  }}
                >
                  UTC
                </Box>
              </Box>
            </Box>
            {hasClientFilters && (
              <Box
                onClick={handleClearFilters}
                sx={{
                  cursor: "pointer",
                  color: "#142C5C",
                  fontSize: { xs: "12px", sm: "13px", md: "14px" },
                  textDecoration: "underline",
                  "&:hover": { color: "#0f2247" },
                }}
              >
                Clear Filters
              </Box>
            )}
          </Box>

          <Box
            sx={{
              marginBottom: "15px",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setShowStats(!showStats)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                backgroundColor: "#f5f5f5",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#ebebeb" },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "13px", sm: "14px", md: "15px" },
                  color: "#142C5C",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                Demographic Summary
                {statsLoading ? (
                  <> — Loading all users…</>
                ) : (
                  <>
                    {" "}
                    — {demographicStats.total.toLocaleString()} matching filters
                    {hasClientFilters
                      ? ` (of ${totalUserCount.toLocaleString()} in search/status)`
                      : ` · ${totalUserCount.toLocaleString()} total`}
                  </>
                )}
              </Typography>
              {statsLoading && (
                <CircularProgress size={18} sx={{ mr: 1, color: "#142C5C" }} />
              )}
              <IconButton size="small" sx={{ color: "#142C5C" }}>
                {showStats ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={showStats}>
              {statsLoading ? (
                <StatsLoadingPlaceholder />
              ) : (
              <Box
                sx={{
                  padding: "16px",
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: "24px",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      marginBottom: "12px",
                      color: "#333",
                    }}
                  >
                    Gender Distribution
                  </Typography>
                  {Object.entries(demographicStats.genderCounts).map(
                    ([gender, count]) => {
                      if (count === 0) return null;
                      const percentage =
                        demographicStats.total > 0
                          ? ((count / demographicStats.total) * 100).toFixed(1)
                          : 0;
                      const maxCount = Math.max(
                        ...Object.values(demographicStats.genderCounts),
                      );
                      const barWidth =
                        maxCount > 0 ? (count / maxCount) * 100 : 0;
                      return (
                        <Box key={gender} sx={{ marginBottom: "8px" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "4px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: "11px",
                                  sm: "12px",
                                  md: "13px",
                                },
                                textTransform: "capitalize",
                                color: "#555",
                              }}
                            >
                              {gender}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: "11px",
                                  sm: "12px",
                                  md: "13px",
                                },
                                color: "#777",
                              }}
                            >
                              {count} ({percentage}%)
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={barWidth}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: "#e0e0e0",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 4,
                                backgroundColor:
                                  gender === "male"
                                    ? "#2196F3"
                                    : gender === "female"
                                      ? "#E91E63"
                                      : "#9C27B0",
                              },
                            }}
                          />
                        </Box>
                      );
                    },
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      marginBottom: "12px",
                      color: "#333",
                    }}
                  >
                    Age Distribution
                  </Typography>
                  {Object.entries(demographicStats.ageCounts).map(
                    ([bracket, count]) => {
                      if (count === 0) return null;
                      const percentage =
                        demographicStats.total > 0
                          ? ((count / demographicStats.total) * 100).toFixed(1)
                          : 0;
                      const maxCount = Math.max(
                        ...Object.values(demographicStats.ageCounts),
                      );
                      const barWidth =
                        maxCount > 0 ? (count / maxCount) * 100 : 0;
                      return (
                        <Box key={bracket} sx={{ marginBottom: "8px" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "4px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: "11px",
                                  sm: "12px",
                                  md: "13px",
                                },
                                color: "#555",
                              }}
                            >
                              {bracket}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: "11px",
                                  sm: "12px",
                                  md: "13px",
                                },
                                color: "#777",
                              }}
                            >
                              {count} ({percentage}%)
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={barWidth}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: "#e0e0e0",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 4,
                                backgroundColor: "#142C5C",
                              },
                            }}
                          />
                        </Box>
                      );
                    },
                  )}
                </Box>
              </Box>
              )}
            </Collapse>
          </Box>

          <Box sx={{ position: "relative", width: "100%", minWidth: 0 }}>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.6)",
                  zIndex: 2,
                }}
              >
                <CircularProgress size={32} sx={{ color: "#142C5C" }} />
              </Box>
            )}
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.stickyHeader}>
                  <TableCell sx={{ ...headerCellStyle, width: "11%" }}>
                    First Name
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "11%" }}>
                    Last Name
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "12%" }}>
                    Username
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "18%" }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "12%" }}>
                    Phone
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "7%" }}>
                    Age
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "9%" }}>
                    Gender
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "10%" }}>
                    Registered ({timeZone})
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "10%" }}>
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      ...headerCellStyle,
                      width: "8%",
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length ? (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell sx={bodyCellStyle}>
                        {user?.firstName || "-"}
                      </TableCell>
                      <TableCell sx={bodyCellStyle}>
                        {user?.lastName || "-"}
                      </TableCell>
                      <TableCell sx={bodyCellStyle}>
                        {user.username || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...bodyCellStyle,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {user.email || "-"}
                      </TableCell>
                      <TableCell sx={bodyCellStyle}>
                        {user.phoneNumber || "-"}
                      </TableCell>
                      <TableCell sx={bodyCellStyle}>
                        {user.age || "-"}
                      </TableCell>
                      <TableCell
                        sx={{ ...bodyCellStyle, textTransform: "capitalize" }}
                      >
                        {user.gender || "-"}
                      </TableCell>
                      <TableCell sx={bodyCellStyle}>
                        {timeZone === "UTC"
                          ? dayjs(user.createdAt).utc().format("MMM D, YYYY h:mm A")
                          : dayjs(user.createdAt)
                              .tz("America/New_York")
                              .format("MMM D, YYYY h:mm A")}
                      </TableCell>
                      <TableCell sx={bodyCellStyle}>
                        <CustomSwitch
                          checked={!user.isDisabled}
                          onChange={() => handleStatusChange(user?.id)}
                          isUser={true}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ ...bodyCellStyle, textAlign: "center" }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/manage-users/view-user/${user.id}`)
                          }
                          title="View Votes"
                          sx={{ color: "#142C5C" }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      sx={{
                        textAlign: "center",
                        py: 6,
                        fontSize: { xs: "12px", sm: "13px", md: "14px" },
                        color: "#333",
                      }}
                    >
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          <Box sx={{ mt: 2 }}>
            <CommonPagination
              count={totalUserCount}
              page={page}
              rowsPerPage={PAGE_SIZE}
              onPageChange={(_, newPage) => setPage(newPage)}
            />
          </Box>
        </Box>
      </>
    </PageLayout>
  );
};

export default ManageUsers;
